import { ListChecks, MessagesSquare, Monitor, Users, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { CanvasCapture, Persona } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { FixListCard } from '../fix-list/room-fix-list';
import { ArrowLeftIcon } from '../lib/icons';
import { MobileHeaderSlot } from '../lib/mobile-header-slot';
import { PersonaAvatar } from '../persona/persona-avatar';
import { PersonaCard } from '../research-room/persona-card';
import { useResearchRoomStore } from '../store/research-room-store';

type Tab = 'fixes' | 'page' | 'personas' | 'room';

const TABS: { icon: typeof Users; id: Tab; label: string }[] = [
  { icon: Monitor, id: 'page', label: 'Page' },
  { icon: Users, id: 'personas', label: 'Personas' },
  { icon: MessagesSquare, id: 'room', label: 'Room' },
  { icon: ListChecks, id: 'fixes', label: 'Fixes' },
];

/**
 * A session on a phone: the board's parts, one at a time, behind a bottom
 * menu — the page, who is on the panel, the room, and its fix list.
 *
 * Every tab stays mounted and is only hidden, so the room keeps its stream
 * and its scroll while somebody looks at the page.
 *
 * What the board does with a fix list — "Open fix list" in the room — the
 * phone does by opening the Fixes tab.
 */
export function MobileSession({
  captures,
  contextCapture,
  onExit,
  personas,
  room,
  roomReady,
}: {
  captures: CanvasCapture[];
  /** The screen the room is about: whose fix list the Fixes tab shows. */
  contextCapture: CanvasCapture | null;
  onExit: () => void;
  /** Who is in the room, or who will be. */
  personas: Persona[];
  /** The room panel, in its phone layout. */
  room: ReactNode;
  /** Whether the room exists yet — the fix list is its. */
  roomReady: boolean;
}) {
  const [tab, setTab] = useState<Tab>('room');
  /** Where the room puts End session — see `MobileHeaderSlot`. */
  const [actions, setActions] = useState<HTMLElement | null>(null);

  // Only requests made from now on: the store outlives the page, and a fix
  // list opened in an earlier room is not a reason to leave this one.
  const analysisView = useResearchRoomStore((s) => s.analysisView);
  const seenView = useRef(analysisView?.key);
  useEffect(() => {
    if (!analysisView || analysisView.key === seenView.current) return;
    seenView.current = analysisView.key;
    if (analysisView.action === 'show') setTab('fixes');
  }, [analysisView]);

  return (
    <MobileHeaderSlot.Provider value={actions}>
      <div className="group/session flex h-svh flex-col bg-warm-alt">
        {/* The room's own header, folded into the app bar: the room needs
            every row of the screen it can get. */}
        <header className="flex h-12 shrink-0 items-center gap-1 border-b border-hairline bg-surface px-1.5">
          <button
            aria-label="All rooms"
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-primary transition active:bg-surface-hover"
            onClick={onExit}
            type="button"
          >
            <ArrowLeftIcon aria-hidden="true" className="size-4" />
          </button>
          <h1 className="!m-0 min-w-0 flex-1 truncate !text-ui-strong !font-semibold !text-text-primary">
            Feedback Room
          </h1>
          <div className="flex shrink-0 items-center" ref={setActions} />
        </header>
        <DesktopNotice />

        <main className="relative min-h-0 flex-1">
          <TabPanel active={tab === 'page'}>
            <PageTab captures={captures} />
          </TabPanel>
          <TabPanel active={tab === 'personas'}>
            <PersonasTab personas={personas} />
          </TabPanel>
          <TabPanel active={tab === 'room'} scroll={false}>
            {room}
          </TabPanel>
          <TabPanel active={tab === 'fixes'}>
            <div className="p-3">
              {contextCapture && roomReady ? (
                <FixListCard
                  anchor={{ x: 0, y: 0 }}
                  captureId={contextCapture.id}
                  docked
                  page={{
                    name: contextCapture.name,
                    url: contextCapture.pageUrl,
                  }}
                  projectId={contextCapture.projectId}
                  zoom={1}
                />
              ) : (
                <p className="p-6 text-center text-ui text-text-secondary">
                  Fixes appear once the room has talked about your page.
                </p>
              )}
            </div>
          </TabPanel>
        </main>

        {/* An app's tab bar: edge to edge and slim — it only switches. Gone
            while typing: the keyboard is up, and every row above it is the
            thread's. */}
        <nav
          aria-label="Room"
          className="flex shrink-0 border-t border-hairline bg-surface pb-[env(safe-area-inset-bottom)] group-has-[textarea:focus]/session:hidden"
        >
          {TABS.map(({ icon: Icon, id, label }) => {
            const active = tab === id;
            return (
              <button
                aria-current={active ? 'page' : undefined}
                className={[
                  'flex flex-1 cursor-pointer flex-col items-center gap-0.5 pt-1.5 pb-1 text-tag leading-none font-semibold transition active:bg-surface-hover',
                  active ? 'text-text-primary' : 'text-text-muted',
                ].join(' ')}
                key={id}
                onClick={() => {
                  if (id !== tab) trackEvent('mobile_tab_opened', { tab: id });
                  setTab(id);
                }}
                type="button"
              >
                <Icon
                  aria-hidden="true"
                  className={`size-5 ${active ? 'text-brand' : ''}`}
                  strokeWidth={active ? 2.25 : 1.75}
                />
                {label}
              </button>
            );
          })}
        </nav>
      </div>
    </MobileHeaderSlot.Provider>
  );
}

function TabPanel({
  active,
  children,
  scroll = true,
}: {
  active: boolean;
  children: ReactNode;
  scroll?: boolean;
}) {
  return (
    <section
      className={[
        'absolute inset-0',
        scroll ? 'overflow-y-auto' : '',
        // Invisible rather than `display: none`: a hidden tab keeps its size,
        // so the room's thread can still follow its stream and measure what
        // is below, and is where it was left when its tab comes back.
        active ? '' : 'invisible',
      ].join(' ')}
    >
      {children}
    </section>
  );
}

/** The page the room is looking at: its captures, top to bottom. */
function PageTab({ captures }: { captures: CanvasCapture[] }) {
  if (captures.length === 0) {
    return (
      <p className="p-6 text-center text-ui text-text-secondary">
        No captures yet.
      </p>
    );
  }
  return (
    <div className="space-y-4 p-3">
      {captures.map((capture) => (
        <figure className="m-0" key={capture.id}>
          <figcaption className="truncate px-1 pb-1.5 text-caption font-medium text-text-secondary">
            {capture.name ?? capture.pageUrl}
          </figcaption>
          <img
            alt={capture.name ?? 'Captured page'}
            className="block w-full rounded-card bg-surface ring-1 ring-hairline"
            src={capture.imageUrl}
          />
        </figure>
      ))}
    </div>
  );
}

/** Who is on the panel; one tap opens their profile. */
function PersonasTab({ personas }: { personas: Persona[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = personas.find((persona) => persona.id === openId);

  if (open) {
    return (
      <div className="flex h-full flex-col p-3">
        <PersonaCard
          onClose={() => {
            setOpenId(null);
          }}
          persona={open}
        />
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <p className="p-6 text-center text-ui text-text-secondary">
        Floto is writing a panel for this page. It takes a few seconds.
      </p>
    );
  }

  return (
    <ul className="m-0 list-none space-y-2 p-3">
      {personas.map((persona) => (
        <li key={persona.id}>
          <button
            className="flex w-full cursor-pointer items-center gap-3 rounded-card bg-surface p-3 text-left ring-1 ring-hairline transition active:bg-surface-hover"
            onClick={() => {
              trackEvent('persona_profile_opened', { source: 'mobile' });
              setOpenId(persona.id);
            }}
            type="button"
          >
            <PersonaAvatar persona={persona} size="lg" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-ui-strong font-semibold text-text-primary">
                {persona.name}
              </span>
              <span className="block truncate text-caption text-text-secondary">
                {persona.title}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

const NOTICE_KEY = 'floto.desktopNoticeDismissed';

/** Remembered for this tab even when storage is blocked. */
let noticeDismissed = false;

function readDismissed(): boolean {
  if (noticeDismissed) return true;
  try {
    return window.localStorage.getItem(NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The phone layout is the core of a room, not all of it. Closed once, it
 * stays closed — across rooms, tabs and visits.
 */
export function DesktopNotice() {
  const [closed, setClosed] = useState(readDismissed);
  if (closed) return null;
  return (
    <div
      className="flex shrink-0 items-center gap-2 bg-ink px-3 py-2 text-caption text-white"
      role="note"
    >
      <Monitor aria-hidden="true" className="size-4 shrink-0" />
      <p className="m-0 min-w-0 flex-1">
        For the full Floto experience, use a desktop.
      </p>
      <button
        aria-label="Dismiss"
        className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-button"
        onClick={() => {
          noticeDismissed = true;
          try {
            window.localStorage.setItem(NOTICE_KEY, '1');
          } catch {
            // Private mode: the module flag still covers this visit.
          }
          setClosed(true);
        }}
        type="button"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
